import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage, pickLang } from "../contexts/LanguageContext";
import { T } from "../i18n/strings";
import { ArticleCard } from "../components/ArticleCard";

export default function Article() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <p className="max-w-3xl mx-auto py-24 text-center font-mono-meta text-xs uppercase tracking-widest text-[#64748B]" data-testid="loading">Loading...</p>;
  if (notFound || !data) return <p className="max-w-3xl mx-auto py-24 text-center font-serif-display text-3xl text-[#081422]" data-testid="not-found">404</p>;

  const { article, related } = data;
  const title = pickLang(article, "title", lang);
  const dek = pickLang(article, "dek", lang);
  const body = pickLang(article, "body", lang);
  const summary = pickLang(article, "summary", lang);
  const bio = pickLang(article, "author_bio", lang);
  const sectionLabel = T.nav[article.section]?.[lang] || article.section;
  const dateStr = new Date(article.published_at).toLocaleDateString(
    lang === "pt" ? "pt-BR" : "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );

  return (
    <article className="pw-fade-up" data-testid="article-page">
      {/* Hero image full-width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="pw-window-hero overflow-hidden">
          <img src={article.hero_image_url} alt={title} className="w-full aspect-[21/9] object-cover" />
        </div>
        {article.hero_image_credit && (
          <p className="font-mono-meta text-[0.65rem] uppercase tracking-widest text-[#64748B] mt-2 text-right">
            {article.hero_image_credit}
          </p>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        {/* Header */}
        <div className="pw-eyebrow mb-4">
          <Link to={`/section/${article.section}`} className="hover:text-[#DAA520]" data-testid="article-section-link">
            {sectionLabel}
          </Link>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#081422] leading-[1.05] tracking-tight" data-testid="article-title">
          {title}
        </h1>
        {dek && (
          <p className="font-serif-body text-xl sm:text-2xl text-[#475569] mt-5 leading-relaxed" data-testid="article-dek">
            {dek}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-b border-[#C5A059]/40 py-4 font-mono-meta text-[0.7rem] uppercase tracking-widest text-[#64748B]">
          <span data-testid="article-author">{article.author_name}</span>
          <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
          <span data-testid="article-date">{dateStr}</span>
          <span className="w-1 h-1 rounded-full bg-[#C5A059]" />
          <span>{article.reading_time} {T.labels.minRead[lang]}</span>
        </div>

        {summary && (
          <aside className="mt-8 p-6 bg-[#F7F2E6] border-l-4 border-[#C5A059]" data-testid="article-summary">
            <div className="pw-eyebrow mb-2">{lang === "pt" ? "Resumo" : "Executive Summary"}</div>
            <p className="font-serif-body text-lg text-[#0F2137] leading-relaxed">{summary}</p>
          </aside>
        )}

        <div className="mt-10 pw-article-body pw-prose" data-testid="article-body">
          {body.split(/\n+/).map((p, i) => (
            <p key={i} className={i === 0 ? "pw-drop-cap" : ""}>{p}</p>
          ))}
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2" data-testid="article-tags">
            {article.tags.map((tag) => (
              <span key={tag} className="font-mono-meta text-[0.65rem] uppercase tracking-widest px-3 py-1 border border-[#081422]/15 text-[#475569]">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Author */}
        {(bio || article.author_name) && (
          <div className="mt-12 pt-8 border-t border-[#081422]/15 flex gap-4 items-start" data-testid="article-author-block">
            <div className="w-16 h-16 flex-shrink-0 border-2 border-[#C5A059] flex items-center justify-center bg-[#F7F2E6]">
              <span className="font-serif-display text-xl text-[#081422]">
                {article.author_name?.[0] || "E"}
              </span>
            </div>
            <div>
              <div className="pw-eyebrow mb-1">{T.labels.author[lang]}</div>
              <h4 className="font-serif-display text-xl font-semibold text-[#081422]">{article.author_name}</h4>
              {bio && <p className="font-serif-body text-[#475569] mt-1 leading-relaxed">{bio}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Related */}
      {related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-[#081422]/10">
          <div className="pw-editorial-divider mb-8">
            <span>{T.labels.related[lang]}</span>
            <span>{related.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {related.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </div>
      )}
    </article>
  );
}
